/** Services Import */
import Commons from './Services/Common/Common';
/** React */
import React from 'react';
/** Components Import */
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Routers from './Routers/Router';
import ErrorBoundary from './Error/Demo';
/** ag-Grid */
import { LicenseManager } from 'ag-grid-enterprise';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SnackbarBasic from './Components/MUIs/SnackbarBasic';
import UpdateDialog from './Components/Ui/UpdateDialog';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Loading } from './Components/MUIs';
import { Capacitor } from '@capacitor/core';
import Echo from './plugins/echo/echo';
import { iosPlugin } from './plugins/iosPlugin/iosPlugin';
import { SystemBarActions } from './Reducer/SystemBarReducer';
import { useDispatch } from 'react-redux';
// import SplashScreen from './Components/Ui/SplashScreen';

dayjs.locale('ko');

LicenseManager.setLicenseKey(process.env.REACT_APP_AgGrid_License);
const theme = createTheme({
  typography: {
    fontFamily:
      "'SpoqaHanSansNeo','Pretendard', 'Roboto', 'Helvetica', 'Arial', sans-serif",
  },
  palette: {
    // mode: "dark",
    // background: {
    // 	default: "#090e23",
    // 	paper: "#090e23",
    // },
    primary: {
      light: '#2CA900',
      main: '#2CA900',
      dark: '#2CA900',
      contrastText: '#fff',
    },
    // secondary: {
    // 	light: "#ff7961",
    // 	main: "#f44336",
    // 	dark: "#ba000d",
    // 	contrastText: "#000",
    // },
  },
});

function HandleError(error, errorInfo) {
  console.error('Unhandled UI error:', error, errorInfo);
}

function App() {
  // const [showSplash, setShowSplash] = React.useState(true);
  const meta = document.createElement('meta');
  const dispatch = useDispatch();
  meta.name = 'viewport';
  meta.content = 'width=device-width, initial-scale=1';
  document.getElementsByTagName('head')[0].appendChild(meta);

  // 로컬 스토리지 버전 관리
  Commons.Storages('set', 'version', {
    version: 'v1.2.6',
  });

  // 로컬 스토리지 권한 관리
  if (
    !Commons.Storages('get', 'auth') ||
    !Commons.Storages('get', 'auth').auth
  ) {
    Commons.Storages('set', 'auth', {
      auth: false,
    });
  }

  // 로컬 스토리지 작업 시작 여부 관리
  if (
    !Commons.Storages('get', 'works') ||
    !Commons.Storages('get', 'works').workYn
  ) {
    Commons.Storages('set', 'works', {
      workYn: false,
    });
  }

  // 로컬 스토리지 Settings 관리
  const settings = Commons.Storages('get', 'settings');
  if (!settings) {
    // settings가 아예 없을 때만 초기화
    Commons.Storages('set', 'settings', {
      language: 'kr',
      userId: '',
      lanCd: 'Kr',
      userIdKeep: false,
      loginKeep: false,
    });
  } else {
    // settings가 있으면 누락된 필드만 기본값으로 설정 (기존 값은 유지)
    const updatedSettings = {
      language: settings.language || 'kr',
      userId: settings.userId || '',
      lanCd: settings.lanCd || 'Kr',
      userIdKeep:
        settings.userIdKeep !== undefined ? settings.userIdKeep : false,
      loginKeep: settings.loginKeep !== undefined ? settings.loginKeep : false,
    };
    // 기존에 있던 다른 필드들도 유지 (예: userNm)
    Commons.Storages('set', 'settings', {
      ...settings,
      ...updatedSettings,
    });
  }

  // 로컬 스토리지 경로관리
  if (
    !Commons.Storages('get', 'path') ||
    !Commons.Storages('get', 'path').nowPath
  ) {
    Commons.Storages('set', 'path', {
      nowPath: 1,
    });
  }

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });

  // 모바일 기기 statusBar(상단 시스템UI), navbar(하단 시스템UI) 높이 구하는 함수
  const DeviceEdge = async () => {
    if (Capacitor.isNativePlatform()) {
      if (Capacitor.getPlatform() === 'android') {
        const result = await Echo.getSystemBarInsets();
        dispatch(
          SystemBarActions.setSystemBarInsets({
            statusBarHeight: result.statusBarHeight,
            navBarHeight: result.navBarHeight,
          })
        );
      } else if (Capacitor.getPlatform() === 'ios') {
        const result = await iosPlugin.getSystemBarInsets();
        dispatch(
          SystemBarActions.setSystemBarInsets({
            statusBarHeight: result.statusBarHeight,
            navBarHeight: result.navBarHeight,
          })
        );
      }
    }
  };

  React.useEffect(() => {
    const LINK_ID = 'material-symbols-outlined-font';
    const HREF =
      'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght@100..700&display=swap';

    if (document.getElementById(LINK_ID)) return;

    const link = document.createElement('link');
    link.id = LINK_ID;
    link.rel = 'stylesheet';
    link.href = HREF;
    document.head.appendChild(link);

    DeviceEdge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ErrorBoundary onError={HandleError}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
            <Loading />
            <SnackbarBasic />
            <Routers />
            <UpdateDialog />
          </LocalizationProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
